import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Lock, Unlock, CheckCircle } from "lucide-react";
import { ButtonPrimary } from "@components";
import { useTranslation } from "react-i18next";
import styles from "./NonLoggedInAnimation.module.scss";

// Import detector logos
import GPTZeroLogo from "./assets/svg/GPTZero_color.svg";
import ZeroGPTLogo from "./assets/svg/ZeroGPT_color.webp";
// import CopyLeaksLogo from "./assets/svg/copyleaks_color.png";
// import SaplingLogo from "./assets/svg/sapling_color.svg";
import WinstonLogo from "./assets/svg/Winston_color.svg";
import OriginalityLogo from "./assets/svg/originality_color.svg";


interface NonLoggedInAnimationProps {
	onUnlock: () => void;
	onAnimationComplete?: () => void;
	inputText?: string;
}

export const NonLoggedInAnimation: React.FC<NonLoggedInAnimationProps> = ({
	onUnlock,
	onAnimationComplete,
	inputText = "This text has been processed to bypass AI detection. The content appears natural and flows smoothly, making it indistinguishable from human-written text. Advanced algorithms ensure the output maintains coherence and context while avoiding patterns typically associated with AI generation.",
}) => {
	const { t } = useTranslation();
	const [animationStage, setAnimationStage] = useState(0);

	// Preload images to prevent flickering in production
	useEffect(() => {
		const imagesToPreload = [GPTZeroLogo, ZeroGPTLogo, WinstonLogo, OriginalityLogo];
		imagesToPreload.forEach((imageSrc) => {
			const img = new Image();
			img.src = imageSrc;
		});
	}, []);

	// Animation sequence timing (in milliseconds)
	const STAGE_DURATIONS = [3000, 4000, 6000, 300]; // Extended detection stage to 6 seconds
	const FINAL_STAGE_INDEX = 4; // Added an extra stage for AI detection results
	const [isComplete, setIsComplete] = useState(false);
	const [shouldPersistFinalStage, setShouldPersistFinalStage] = useState(false);

	const [progress, setProgress] = useState(0);
	const [generatedText, setGeneratedText] = useState("");
	const [showBlurredText, setShowBlurredText] = useState(false);
	const [detectionResults, setDetectionResults] = useState([
		{
			name: "GPTZero",
			logo: GPTZeroLogo,
			score: 98,
		},
		{
			name: "ZeroGPT",
			logo: ZeroGPTLogo,
			score: 97,
		},
		{
			name: "Winston",
			logo: WinstonLogo,
			score: 99,
		},
		{
			name: "Originality",
			logo: OriginalityLogo,
			score: 96,
		},
	]);

	const sampleText = inputText || "";

	// Get first two words of the input text for personalization
	const getFirstTwoWords = (text: string): string => {
		const words = text.trim().split(/\s+/);
		if (words.length < 2) return text.substring(0, 20);
		return `${words[0]} ${words[1]}`;
	};

	// This effect handles the entire animation sequence
	useEffect(() => {
		// Skip animation if we're already complete
		if (isComplete) {
			setAnimationStage(FINAL_STAGE_INDEX);
			setShouldPersistFinalStage(true);
			return;
		}

		// Don't restart animation if we're already in progress
		if (animationStage > 0 && animationStage < FINAL_STAGE_INDEX) {
			return;
		}

		let isMounted = true;
		let timeouts: NodeJS.Timeout[] = [];

		// Start with stage 0 (Processing)
		setAnimationStage(0);
		setProgress(0);

		// Simulate progress for stage 0
		const processingInterval = setInterval(() => {
			if (!isMounted) return;

			setProgress((prev) => {
				const newValue = prev + 1;
				if (newValue >= 100) {
					clearInterval(processingInterval);
				}
				return newValue > 100 ? 100 : newValue;
			});
		}, 30);

		// Move to stage 1 (Generating) after STAGE_DURATIONS[0]
		timeouts.push(
			setTimeout(() => {
				if (!isMounted) return;
				clearInterval(processingInterval);
				setAnimationStage(1);
				setProgress(0); // Reset progress for next stage
			}, STAGE_DURATIONS[0]),
		);

		// Move to stage 2 (AI Detection) after stage 1 duration
		timeouts.push(
			setTimeout(
				() => {
					if (!isMounted) return;
					setAnimationStage(2);
					setProgress(0); // Reset progress for next stage
					setCompletedDetectors([]); // Reset completed detectors
				},
				STAGE_DURATIONS[0] + STAGE_DURATIONS[1] + 1000,
			),
		); // Add 1s buffer

		// Remove the fixed timeout for the final stage transition
		// Instead, we'll use the completedDetectors state in the AI detection effect

		// Cleanup function
		return () => {
			isMounted = false;
			clearInterval(processingInterval);
			timeouts.forEach(clearTimeout);
		};
	}, [isComplete]);

	// This effect triggers the onAnimationComplete callback once the final stage is set
	useEffect(() => {
		if (animationStage === FINAL_STAGE_INDEX && onAnimationComplete) {
			// Don't call onAnimationComplete automatically - let the final stage persist
			// The parent will handle state changes only when user clicks unlock
			console.log("Final stage reached, but keeping animation alive");
		}
	}, [animationStage, onAnimationComplete]);

	// Track which detectors have completed their check
	const [completedDetectors, setCompletedDetectors] = useState<string[]>([]);

	// Handle AI detection animation for stage 2
	useEffect(() => {
		if (animationStage === 2) {
			// Reset state
			setCompletedDetectors([]);
			setProgress(0);

			let isMounted = true;
			const timeouts: NodeJS.Timeout[] = [];

			// Progress bar animation - use requestAnimationFrame for smoother animation
			let lastTimestamp = 0;
			let animationFrameId: number;
			const totalDuration = 7000; // 7 seconds to match the last detector
			const startTime = Date.now();

			const updateProgressBar = () => {
				if (!isMounted) return;

				const elapsed = Date.now() - startTime;
				const newProgress = Math.min(100, (elapsed / totalDuration) * 100);

				setProgress(newProgress);

				if (newProgress < 100) {
					animationFrameId = requestAnimationFrame(updateProgressBar);
				}
			};

			animationFrameId = requestAnimationFrame(updateProgressBar);

			// Add cleanup for the animation frame
			timeouts.push(setTimeout(() => {}, 0)); // Dummy timeout for cleanup tracking

			// Fixed completion times for each detector
			timeouts.push(
				setTimeout(() => {
					if (!isMounted) return;
					setCompletedDetectors((prev) => [...prev, "Winston"]);
				}, 2500),
			);

			timeouts.push(
				setTimeout(() => {
					if (!isMounted) return;
					setCompletedDetectors((prev) => [...prev, "GPTZero"]);
				}, 4000),
			);

			timeouts.push(
				setTimeout(() => {
					if (!isMounted) return;
					setCompletedDetectors((prev) => [...prev, "ZeroGPT"]);
				}, 5500),
			);

			timeouts.push(
				setTimeout(() => {
					if (!isMounted) return;
					setCompletedDetectors((prev) => [...prev, "Originality"]);

					// After the last detector completes, wait a brief moment then move to final stage
					timeouts.push(
						setTimeout(() => {
							if (!isMounted) return;
							setAnimationStage(FINAL_STAGE_INDEX);
							setIsComplete(true);
							setShouldPersistFinalStage(true);
						}, 1000),
					); // Wait just 1 second after all detectors are complete
				}, 7000),
			);

			// Cleanup function
			return () => {
				isMounted = false;
				if (animationFrameId) {
					cancelAnimationFrame(animationFrameId);
				}
				timeouts.forEach(clearTimeout);
			};
		}
	}, [animationStage]);

	// Generate text with typing effect and track progress
	useEffect(() => {
		if (animationStage === 1) {
			// Reset states when stage changes
			setGeneratedText("");
			setShowBlurredText(false);
			setProgress(0);

			let isMounted = true;
			let animationFrameId: number;
			let lastFrameTime = 0;
			let currentIndex = 0;
			const totalLength = inputText.length;

			// Use requestAnimationFrame for smoother animation
			const animateTyping = (timestamp: number) => {
				if (!isMounted) return;

				// Control typing speed - aim for consistent frame rate
				const elapsed = timestamp - lastFrameTime;
				const frameInterval = 7; // Significantly reduced interval for much faster typing animation

				if (elapsed > frameInterval) {
					lastFrameTime = timestamp;

					if (currentIndex <= totalLength) {
						// Update text being typed - increase by multiple characters at once for faster typing
						const charsPerFrame = 3; // Type 3 characters at once
						const newIndex = Math.min(currentIndex + charsPerFrame, totalLength);
						setGeneratedText(inputText.substring(0, newIndex));
						currentIndex = newIndex;

						// Batch updates by updating progress less frequently
						// Only update progress every few characters
						if (currentIndex % 6 === 0 || currentIndex === totalLength) {
							const progressPercentage = Math.min(
								100,
								Math.floor((currentIndex / totalLength) * 100),
							);
							setProgress(progressPercentage);
						}

						animationFrameId = requestAnimationFrame(animateTyping);
					} else {
						// Show blurred text once typing is complete
						setShowBlurredText(true);
						setProgress(100);

						// Force transition to next stage after a short delay
						setTimeout(() => {
							if (!isMounted) return;
							setAnimationStage(2);
						}, 800);
					}
				} else {
					animationFrameId = requestAnimationFrame(animateTyping);
				}
			};

			// Start typing after a short delay
			const startDelay = setTimeout(() => {
				animationFrameId = requestAnimationFrame(animateTyping);
			}, 300);

			return () => {
				isMounted = false;
				if (animationFrameId) {
					cancelAnimationFrame(animationFrameId);
				}
				clearTimeout(startDelay);
			};
		}
	}, [animationStage, inputText]);

	// Render progress bar component with consistent styling
	const progressBarFillRef = useRef<HTMLDivElement>(null);
	const progressTextRef = useRef<HTMLDivElement>(null);

	const updateProgressBarDirectly = (value: number) => {
		const roundedValue = Math.floor(value);

		// Update the width directly with CSS
		if (progressBarFillRef.current) {
			progressBarFillRef.current.style.width = `${roundedValue}%`;
		}

		// Update the text content directly
		if (progressTextRef.current) {
			progressTextRef.current.textContent = `${roundedValue}%`;
		}
	};

	// Render progress bar component with consistent styling
	const renderProgressBar = (value: number) => {
		// Round percentage to integer
		const roundedValue = Math.floor(value);
		return (
			<div className={styles.progressBarContainer}>
				<div className={styles.progressBar}>
					<div
						ref={progressBarFillRef}
						className={styles.progressBarFill}
						style={{ width: `${roundedValue}%` }}
					/>
				</div>
				<div ref={progressTextRef} className={styles.progressText}>
					{roundedValue}%
				</div>
			</div>
		);
	};

	// Render different animation stages
	const renderStage = () => {
		switch (animationStage) {
			case 0: // Initial processing state
				return (
					<div className={styles.animationStage}>
						<div className={styles.stageTitle}>{t("Processing")}</div>
						{renderProgressBar(progress)}
					</div>
				);

			case 1: // Text generation animation
				return (
					<div className={styles.animationStage}>
						<div className={styles.stageTitle}>{t("Generating")}</div>

						<div className={styles.generatingContainer}>
							{!showBlurredText ? (
								<div className={styles.typingContainer}>
									<motion.span
										className={styles.typingText}
										initial={{ filter: "blur(0px)" }}
										animate={{ filter: "blur(6px)" }}
										transition={{ duration: 0.5 }}
									>
										{generatedText}
									</motion.span>
									<span className={styles.typingCursor}></span>
								</div>
							) : (
								<div className={styles.blurredTextContainer}>
									<motion.div
										className={styles.blurredText}
										initial={{ filter: "blur(4px)" }}
										animate={{ filter: "blur(8px)" }}
										transition={{ duration: 1 }}
									>
										{inputText}
									</motion.div>
								</div>
							)}
						</div>
						{renderProgressBar(progress)}
					</div>
				);

			case 2: // AI detection with horizontal layout
				return (
					<div className={styles.animationStage}>
						<div className={styles.stageTitle}>{t("Double checking...")}</div>

						<div className={styles.detectionResults}>
							{detectionResults.map((detector, index) => {
								// Check if this detector has completed its check
								const isComplete = completedDetectors.includes(detector.name);

								return (
									<div key={index} className={styles.detectorResult}>
										{isComplete ? (
											<motion.div
												className={styles.checkmark}
												initial={{ scale: 0, opacity: 0 }}
												animate={{ scale: 1, opacity: 1 }}
												transition={{ duration: 0.3, type: "spring" }}
											>
												✓
											</motion.div>
										) : (
											<motion.div
												className={styles.loadingCircle}
												animate={{ rotate: 360 }}
												transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
											/>
										)}
										<div className={styles.logoContainer}>
											<img
												src={detector.logo}
												alt={detector.name}
												className={styles.detectorLogo}
											/>
										</div>
									</div>
								);
							})}
						</div>

						{renderProgressBar(progress)}

						{completedDetectors.length === detectionResults.length && (
							<motion.div
								className={styles.stageTitle}
								style={{ marginTop: "1.5rem" }}
								initial={{ opacity: 0, scale: 0.9 }}
								animate={{ opacity: 1, scale: 1 }}
								transition={{ duration: 0.5 }}
							>
								{t("Your text is 100% undetectable!")}
							</motion.div>
						)}
					</div>
				);

			case 4: // Final success stage
				return (
					<motion.div 
						className={styles.animationStage}
						variants={{
							hidden: { opacity: 0 },
							visible: {
								opacity: 1,
								transition: {
									staggerChildren: 0.2,
									delayChildren: 0.1
								}
							}
						}}
						initial="hidden"
						animate="visible"
					>
						<div className={styles.finalStage}>
							<motion.div
								className={styles.finalIcon}
								variants={{
									hidden: { scale: 0.8, opacity: 0 },
									visible: { scale: 1, opacity: 1, transition: { duration: 0.5, type: "spring" } }
								}}
							>
								<svg width="300" height="300" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
									<defs>
										<linearGradient id="iconGradient" x1="0%" y1="0%" x2="100%" y2="0%">
											<stop offset="0%" style={{stopColor: '#fd3707'}} />
											<stop offset="100%" style={{stopColor: '#ff5722'}} />
										</linearGradient>
									</defs>
									<path d="M26.6667 16.6667H25V13.3333C25 10.3833 22.6167 8 19.6667 8C16.7167 8 14.3333 10.3833 14.3333 13.3333V16.6667H12.6667C11.75 16.6667 11 17.4167 11 18.3333V28.3333C11 29.25 11.75 30 12.6667 30H26.6667C27.5833 30 28.3333 29.25 28.3333 28.3333V18.3333C28.3333 17.4167 27.5833 16.6667 26.6667 16.6667ZM16.6667 13.3333C16.6667 11.6667 18.0167 10.3333 19.6667 10.3333C21.3167 10.3333 22.6667 11.6667 22.6667 13.3333V16.6667H16.6667V13.3333Z" fill="url(#iconGradient)"/>
								</svg>
							</motion.div>
							<motion.div
								className={styles.finalTitle}
								variants={{
									hidden: { y: 20, opacity: 0 },
									visible: { y: 0, opacity: 1, transition: { duration: 0.5 } }
								}}
							>
								{t("100% Undetectable")}
							</motion.div>
							<motion.div
								className={styles.finalDescription}
								variants={{
									hidden: { y: 20, opacity: 0 },
									visible: { y: 0, opacity: 1, transition: { duration: 0.5 } }
								}}
							>
								{t("Text is clear from AI content")}
							</motion.div>

							<motion.div
								className={styles.firstTwoWordsContainer}
								variants={{
									hidden: { opacity: 0 },
									visible: { opacity: 1, transition: { duration: 0.5 } }
								}}
							>
								<div className={styles.firstTwoWords}>
									{t("Your text")}: "{getFirstTwoWords(inputText)}"
								</div>
								<div className={styles.expiryNote}>{t("Will be removed in 5 minutes")}</div>
							</motion.div>

							<motion.div
								variants={{
									hidden: { scale: 0.9, opacity: 0 },
									visible: { scale: 1, opacity: 1, transition: { duration: 0.5 } }
								}}
								whileHover={{ scale: 1.05 }}
								whileTap={{ scale: 0.95 }}
							>
								<ButtonPrimary onClick={onUnlock} className={styles.unlockButton} colorScheme="orange">
									<Unlock size={20} />
									{t(`humanizer.unlock`)}
								</ButtonPrimary>
							</motion.div>
						</div>
					</motion.div>
				);

			default:
				return null;
		}
	};

	// Main component return
	return (
		<div className={styles.container}>
			<AnimatePresence initial={false}>
				{animationStage !== null && (
					<motion.div
						key="non-logged-animation"
						initial={{ opacity: 0, y: 10 }}
						animate={{ opacity: 1, y: 0 }}
						exit={{ opacity: 0, y: -10 }}
						transition={{ 
							duration: 0.3,
							ease: "easeInOut"
						}}
						className={styles.animationContainer}
					>
						{renderStage()}
					</motion.div>
				)}
			</AnimatePresence>
		</div>
	);
};
